import { createSlice } from '@reduxjs/toolkit';

export type TTab = {
  children: React.ReactNode;
  id: string;
  label: string;
  tooltip: string;
  asaccordion?: boolean;
  open?: boolean;
  marked?: boolean;
  props?: Record<string, unknown>;
};

export type TTabEvent = Pick<TTab, 'id' | 'open'>;

interface IGlobalState {
  lastFocusedTabId: string;
  showMenu?: boolean;
  tabEvent?: TTabEvent;
  tabs: TTab[];
  firstFocused: boolean;
}

const globalSlice = createSlice({
  name: 'globalSlice',
  initialState: {
    state: {
      lastFocusedTabId: '',
      showMenu: false,
      tabs: [],
      firstFocused: false,
    },
  } as { state: IGlobalState },
  reducers: {
    markTab({ state }, { payload: { id } }: { payload: { id: string } }) {
      const tabIndex = state.tabs.findIndex((tab) => tab.id === id);
      if (tabIndex !== -1) {
        state.tabs[tabIndex].marked = true;
      }
    },
    registerTab: (
      { state },
      {
        payload,
      }: {
        payload: {
          insertAfter?: string;
          insertBefore?: string;
          insertAtPosition?: number;
          tab: TTab;
        };
      },
    ) => {
      if (!state.tabs.find((tab) => tab.id === payload.tab.id)) {
        const newTab = { ...payload.tab };

        if (payload.insertAfter) {
          const insertIndex = state.tabs.findIndex(
            (tab) => tab.id === payload.insertAfter,
          );
          if (insertIndex !== -1) {
            const currentTabs = [...state.tabs];
            state.tabs = [
              ...currentTabs.splice(0, insertIndex),
              newTab,
              ...currentTabs,
            ];
            return;
          }
        }

        if (payload.insertBefore) {
          const insertIndex = state.tabs.findIndex(
            (tab) => tab.id === payload.insertBefore,
          );
          if (insertIndex !== -1) {
            const currentTabs = [...state.tabs];
            state.tabs = [
              ...currentTabs.splice(0, insertIndex - 1),
              newTab,
              ...currentTabs,
            ];
            return;
          }
        }

        const position =
          payload.insertAtPosition === undefined
            ? state.tabs.length
            : payload.insertAtPosition;

        const currentTabs = [...state.tabs];
        state.tabs = [
          ...currentTabs.splice(0, position),
          newTab,
          ...currentTabs,
        ];
      }
    },
    toggleTab: (
      { state },
      { payload: { id, open } }: { payload: TTabEvent },
    ) => {
      state.tabEvent = { id, open };
      state.lastFocusedTabId = id;
      state.tabs = state.tabs.map((tab) =>
        tab.id === id ? { ...tab, id, open } : tab,
      );
    },
    setState: (state, { payload }: { payload: Partial<IGlobalState> }) => {
      state.state = {
        ...state.state,
        ...payload,
      };
    },
  },
});

export const globalActions = globalSlice.actions;
export default globalSlice.reducer;
